import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { type ChangeEvent, type Dispatch, type SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DemographicsAgeSegmentsList } from '../../../../../../../../modules/audiences/AudienceForm/components/AudienceCriteria/components/expandableSections/demographics/DemographicsAgeSegmentsList.tsx';
import { handleAddAgeGroup } from '../../../../../../../../modules/audiences/AudienceForm/components/AudienceCriteria/components/utils/handleAddAgeGroup.ts';
import { useDemographicsAgeValidation } from '../../../../../../../../modules/audiences/AudienceForm/components/AudienceCriteria/hooks/useDemographicsAgeValidation.ts';
import {
  ANY_AGE_VALUE,
  useDemographicsOptions,
} from '../../../../../../../../modules/audiences/AudienceForm/components/AudienceCriteria/hooks/useDemographicsOptions.ts';
import type { AudienceListType, FormSegment } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';

export type DemographicsAgeProps = {
  listType: AudienceListType;
  isEdit: boolean;
  selectedAgeSegments: FormSegment[];
  setSelectedAgeSegments: Dispatch<SetStateAction<FormSegment[]>>;
};

export const DemographicsAge = ({
  isEdit,
  listType,
  selectedAgeSegments,
  setSelectedAgeSegments,
}: DemographicsAgeProps) => {
  const { t } = useTranslation();
  const [minAge, setMinAge] = useState<number>(ANY_AGE_VALUE);
  const [maxAge, setMaxAge] = useState<number>(ANY_AGE_VALUE);

  const { rangeToSegments } = useDemographicsOptions();

  const validation = useDemographicsAgeValidation(minAge, maxAge, ANY_AGE_VALUE);

  const handleOnAgeDelete = useCallback(
    ({ min, max }) => {
      setSelectedAgeSegments(prev =>
        prev.filter(segment => {
          const value = Number(segment.name);
          return value < min || value > max;
        })
      );
    },
    [setSelectedAgeSegments]
  );

  const handleAgeChange = useCallback(
    (value: string, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, setAge: (age: number) => void) => {
      event.preventDefault();
      const integerRegex = /^[0-9]*$/;
      if (integerRegex.test(value) || value === '') {
        setAge(value === '' ? ANY_AGE_VALUE : +value);
      }
    },
    []
  );

  const checkIfFormInvalid =
    validation.minAge.error || validation.maxAge.error || minAge === ANY_AGE_VALUE || maxAge === ANY_AGE_VALUE;

  return (
    <>
      <Typography
        variant="h3"
        component="p"
        sx={{
          textTransform: 'uppercase',
          pb: 1,
          pt: 1,
        }}
      >
        {t('Age range')}
      </Typography>
      {isEdit && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', textAlign: 'center' }}>
          <RHFTextField
            name={'minAge'}
            dataTest="min-age"
            label={t('Min Age.')}
            placeholder={t('Any')}
            onChange={(value, event) => handleAgeChange(value, event, setMinAge)}
            valueOverride={minAge === ANY_AGE_VALUE ? '' : minAge}
            fullWidth
            error={validation.minAge.error}
            helperText={validation.minAge.helperText}
          />

          <Box
            sx={{
              ml: 1,
              mr: 1,
            }}
          >
            -
          </Box>

          <RHFTextField
            name={'maxAge'}
            dataTest="max-age"
            label={t('Max Age.')}
            placeholder={t('Any')}
            onChange={(value, event) => handleAgeChange(value, event, setMaxAge)}
            valueOverride={maxAge === ANY_AGE_VALUE ? '' : maxAge}
            fullWidth
            error={validation.maxAge.error}
            helperText={validation.maxAge.helperText}
          />

          <Box
            sx={{
              ml: 1,
            }}
          >
            <Button
              variant="text"
              data-test="add-age-range"
              color="info"
              disabled={checkIfFormInvalid}
              onClick={() =>
                handleAddAgeGroup({
                  minAge,
                  maxAge,
                  rangeToSegments,
                  selectedAgeSegments,
                  setSelectedAgeSegments,
                  setMinAge,
                  setMaxAge,
                })
              }
            >
              {t('Add')}
            </Button>
          </Box>
        </Box>
      )}
      {selectedAgeSegments.length > 0 ? (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <DemographicsAgeSegmentsList
            listType={listType}
            ageSegments={selectedAgeSegments}
            onAgeDelete={handleOnAgeDelete}
          />
        </Box>
      ) : (
        <Typography
          sx={{
            mt: 2,
          }}
        >
          {t('Any age')}
        </Typography>
      )}
    </>
  );
};
